import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, DownloadCloud, Loader2, RefreshCw, Undo2 } from "lucide-react";
import { amt } from "../lib/invoice";
import { useAuth } from "../context/AuthContext";
import ErpMapping from "./ErpMapping";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { new: "yeni", changed: "değişti", same: "aynı", removed: "pasifleştirilecek" };

export default function ErpImportPanel({ month, monthLabel, onImported }) {
  const { can } = useAuth();
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [undoOpen, setUndoOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/erp/status`).then(r => setStatus(r.data))
      .catch(e => setNotice(e?.response?.data?.detail || "Satış veritabanına bağlanılamadı"));
  }, []);

  const fetchPreview = useCallback(async (keepNotice = false) => {
    setLoading(true);
    if (!keepNotice) setNotice("");
    try {
      const { data } = await axios.get(`${API}/erp/preview`, { params: { month } });
      setRows(data);
      setSelected(new Set(data.filter(r => r.status !== "same").map(r => r.erp_key)));
    } catch (e) {
      setRows([]);
      setNotice(e?.response?.data?.detail || "Önizleme alınamadı");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { setRows(null); setSelected(new Set()); }, [month]);

  const summary = useMemo(() => {
    const map = new Map();
    (rows || []).forEach(row => {
      const key = `${row.account}|${row.group}|${row.currency}`;
      const entry = map.get(key) || { ...row, gross_revenue: 0, amount: 0, sale_count: 0, days: 0 };
      entry.gross_revenue += row.gross_revenue;
      entry.amount += row.amount;
      entry.sale_count += row.sale_count;
      entry.days += 1;
      map.set(key, entry);
    });
    return [...map.values()];
  }, [rows]);

  const toggle = key => setSelected(current => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const runImport = async () => {
    setBusy("import");
    try {
      const { data } = await axios.post(`${API}/erp/import`, { month, keys: [...selected] });
      setNotice(`${monthLabel}: ${data.created} yeni, ${data.updated} güncellenmiş kayıt (${data.deactivated || 0} pasif).`);
      await fetchPreview(true);
      onImported?.();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Aktarım başarısız");
    } finally {
      setBusy("");
    }
  };

  const undoImport = async () => {
    setUndoOpen(false);
    setBusy("undo");
    try {
      const { data } = await axios.delete(`${API}/erp/import/${month}`);
      setNotice(`${data.deleted} aktarılmış gelir kaydı silindi.`);
      await fetchPreview(true);
      onImported?.();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Geri alınamadı");
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <section className="panel erp-panel" data-testid="erp-import-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">SATIŞ YAZILIMI AKTARIMI</p>
            <h3>{monthLabel} gelirlerini çek</h3>
            {status
              ? <small className="hint" data-testid="erp-status">
                  Bağlı · {status.host}/{status.database} · salt okunur ({status.readonly_user}) ·
                  {" "}{status.sale_count.toLocaleString("tr-TR")} satış · son satış {status.latest_sale_date}
                </small>
              : <small className="hint">Bağlantı kontrol ediliyor…</small>}
          </div>
          <div className="head-side">
            <button className="outline-btn" data-testid="erp-preview-button" disabled={loading} onClick={fetchPreview}>
              {loading ? <Loader2 size={15} className="spin"/> : <RefreshCw size={15}/>} Önizlemeyi getir
            </button>
            {can("create") && (
              <button className="primary-btn" data-testid="erp-import-button" disabled={!selected.size || busy === "import"} onClick={runImport}>
                {busy === "import" ? <Loader2 size={16} className="spin"/> : <DownloadCloud size={16}/>} {selected.size} kaydı aktar
              </button>
            )}
          </div>
        </div>

        {notice && <div className="notice" data-testid="erp-notice">{notice}</div>}

        {rows && !rows.length && <div className="empty-table" data-testid="erp-empty">Bu dönemde aktarılacak satış bulunamadı.</div>}

        {!!summary.length && (
          <div className="erp-summary" data-testid="erp-summary">
            {summary.map(row => (
              <div className="erp-summary-card" key={`${row.account}-${row.group}-${row.currency}`}
                data-testid={`erp-summary-${row.group}`}>
                <small>{row.account}</small>
                <b>{row.group}</b>
                <span>Ciro <b>{amt(row.gross_revenue, row.currency)}</b></span>
                <span className="net">Komisyon <b>{amt(row.amount, row.currency)}</b></span>
                <small className="hint">{row.days} gün · {row.sale_count} satış</small>
              </div>
            ))}
          </div>
        )}

        {!!rows?.length && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th><th>Tarih</th><th>Hesap</th><th>Gelir grubu</th><th>Tür</th>
                  <th className="right">Satış</th><th className="right">Ciro</th><th className="right">Oran</th>
                  <th className="right">Komisyon</th><th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.erp_key} data-testid={`erp-row-${row.erp_key}`} className={row.status === "same" ? "muted-row" : ""}>
                    <td>
                      <input type="checkbox" data-testid={`erp-select-${row.erp_key}`}
                        checked={selected.has(row.erp_key)} onChange={() => toggle(row.erp_key)}/>
                    </td>
                    <td>{row.transaction_date}</td>
                    <td><b>{row.account}</b></td>
                    <td>{row.group}</td>
                    <td>{row.sale_type}</td>
                    <td className="right">{row.sale_count}</td>
                    <td className="right amount">{amt(row.gross_revenue, row.currency)}</td>
                    <td className="right">%{row.commission_rate}</td>
                    <td className="right amount income">{amt(row.amount, row.currency)}</td>
                    <td><span className={`erp-badge ${row.status}`}>
                      {row.status === "same" && <CheckCircle2 size={11}/>} {STATUS_LABEL[row.status]}
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {can("delete") && !!rows?.length && (
          <button className="outline-btn danger-outline" data-testid="erp-undo-button" disabled={busy === "undo"} onClick={() => setUndoOpen(true)}>
            <Undo2 size={15}/> Bu dönemin aktarımını geri al
          </button>
        )}
      </section>

      {can("groups") && <ErpMapping onSaved={() => rows && fetchPreview()}/>}

      <AlertDialog open={undoOpen} onOpenChange={setUndoOpen}>
        <AlertDialogContent data-testid="erp-undo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Aktarım geri alınsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              {monthLabel} döneminde satış yazılımından aktarılan tüm gelir kayıtları silinir. Elle girdiğin kayıtlara dokunulmaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="erp-undo-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="danger-action" data-testid="erp-undo-confirm" onClick={undoImport}>Evet, geri al</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
